import { useEffect, useMemo } from "react";
import { useFilterContext } from "../contexts/FilterContext";
import type { InsuranceListItemProps } from "../InsuranceListItem";
import InsuranceListItemComponent from "../InsuranceListItem";
import PersuasionComponentComponent from "../PersuasionComponent/PersuasionComponent";
import { Container, EmptyState } from "./PlanCardsList.style";

// Local MyPartner object to avoid circular dependency
const MyPartner = {
  InsuranceListItem: InsuranceListItemComponent,
  PersuasionComponent: PersuasionComponentComponent,
};

interface PlanCardsListProps {
  data?: InsuranceListItemProps[] | { data: InsuranceListItemProps[] };
  csrfToken?: string;
}

const PlanCardsList = ({ data, csrfToken }: PlanCardsListProps) => {
  const { quoteIds, setInitialQuoteIds } = useFilterContext();

  // Handle the data structure - memoized to prevent useEffect re-runs
  const allInsuranceCards = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === "object" && "data" in data) {
      const dataObj = data as { data: InsuranceListItemProps[] };
      return Array.isArray(dataObj.data) ? dataObj.data : [];
    }
    return [];
  }, [data]);

  // Initialize context with all quote IDs from API data - preserve original order
  useEffect(() => {
    const originalQuoteIds = allInsuranceCards
      .map((card) => card.quoteId)
      .filter(Boolean) as string[];
    if (originalQuoteIds.length > 0) {
      setInitialQuoteIds(originalQuoteIds);
    }
  }, [allInsuranceCards, setInitialQuoteIds]);

  // Use context as single source of truth - filter cards based on quoteIds from context
  const insuranceCards = useMemo(() => {
    // Only show cards that match the quoteIds from context
    // If quoteIds is empty, show empty array (not all cards)
    const filtered: InsuranceListItemProps[] = [];
    quoteIds.forEach((quoteId) => {
      const matchingCard = allInsuranceCards.find(
        (card) => card.quoteId === quoteId
      );
      if (matchingCard) {
        filtered.push(matchingCard);
      }
    });
    return filtered;
  }, [quoteIds, allInsuranceCards]);

  return (
    <Container>
      {insuranceCards.length > 0 ? (
        insuranceCards.map((cardProps, index) => {
          // Use quoteId as the key for proper React rendering
          const keyValue = cardProps.quoteId || `fallback-${index}`;

          return (
            <MyPartner.InsuranceListItem
              csrfToken={csrfToken}
              key={keyValue}
              {...cardProps}
            />
          );
        })
      ) : (
        <EmptyState>
          {data ? "No insurance plans available" : "Loading insurance plans..."}
        </EmptyState>
      )}
    </Container>
  );
};

export default PlanCardsList;
